import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { USER_ROLES, ROLE_KEYS, isGovernmentRole } from "../constants/userRoles";

/**
 * Rol con el que el usuario esta operando maps EN ESTA SESION.
 *
 * Mismo modelo que el CMS (en-la-parada-web/src/contexts/activeRoleContext.jsx): el backend manda
 * los roles como STRINGS en el login, aca se guardan como ordinal numerico y se persisten en
 * localStorage para que un refresh no devuelva al selector.
 *
 * Un usuario puede tener varios roles permitidos (p.ej. EMPLOYEE + GOVERNMENT_ADMIN) y lo que ve
 * en el mapa depende de CUAL eligio, no de los que tiene.
 */
const ActiveRoleContext = createContext();

const STORAGE_ACTIVE_ROLE = "activeRoleId";
const STORAGE_AVAILABLE_ROLES = "availableRoles";

// Lectura inicial sincronica: si se hiciera en un useEffect, el guard de rutas correria una vez
// con activeRoleId = null y mandaria al selector a un usuario que ya habia elegido.
const readStoredActiveRoleId = () => {
  const stored = localStorage.getItem(STORAGE_ACTIVE_ROLE);
  if (stored === null) return null;
  const parsed = parseInt(stored, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

const readStoredAvailableRoles = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_AVAILABLE_ROLES));
    return Array.isArray(stored) ? stored : [];
  } catch (error) {
    console.error("Error leyendo availableRoles de localStorage:", error);
    return [];
  }
};

export const ActiveRoleProvider = ({ children }) => {
  const [activeRoleId, setActiveRoleId] = useState(readStoredActiveRoleId);
  const [availableRoles, setAvailableRoles] = useState(readStoredAvailableRoles);

  /** Acepta la key string ("GOVERNMENT_ADMIN") o el ordinal (9). */
  const setActiveRole = useCallback((role) => {
    const roleId = typeof role === "string" ? USER_ROLES[role] : role;
    if (roleId === undefined || roleId === null) {
      console.error("Rol invalido al fijar el rol activo:", role);
      return;
    }
    setActiveRoleId(roleId);
    localStorage.setItem(STORAGE_ACTIVE_ROLE, roleId.toString());
  }, []);

  const setAvailableRolesData = useCallback((roles) => {
    const list = Array.isArray(roles) ? roles : [];
    setAvailableRoles(list);
    localStorage.setItem(STORAGE_AVAILABLE_ROLES, JSON.stringify(list));
  }, []);

  /** Solo el rol activo: lo usa "Cambiar rol" para volver al selector sin cerrar sesion. */
  const clearSelection = useCallback(() => {
    setActiveRoleId(null);
    localStorage.removeItem(STORAGE_ACTIVE_ROLE);
  }, []);

  /** Todo: lo usa el logout. */
  const clearActiveRole = useCallback(() => {
    setActiveRoleId(null);
    setAvailableRoles([]);
    localStorage.removeItem(STORAGE_ACTIVE_ROLE);
    localStorage.removeItem(STORAGE_AVAILABLE_ROLES);
  }, []);

  const activeRoleKey = useMemo(
    () => (activeRoleId !== null ? ROLE_KEYS[activeRoleId] : null),
    [activeRoleId]
  );

  const value = useMemo(
    () => ({
      activeRoleId,
      activeRoleKey,
      availableRoles,
      // Atajo para las vistas: con rol de alcaldia se filtra por su govId; con cualquier otro
      // rol permitido (hoy EMPLOYEE) se ve todo.
      isGovernmentView: isGovernmentRole(activeRoleKey),
      setActiveRole,
      setAvailableRoles: setAvailableRolesData,
      clearSelection,
      clearActiveRole,
    }),
    [
      activeRoleId,
      activeRoleKey,
      availableRoles,
      setActiveRole,
      setAvailableRolesData,
      clearSelection,
      clearActiveRole,
    ]
  );

  return (
    <ActiveRoleContext.Provider value={value}>{children}</ActiveRoleContext.Provider>
  );
};

export const useActiveRole = () => useContext(ActiveRoleContext);

export { ActiveRoleContext };
